import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#cleanupSwipeToClose();
    this.#teardownPopoverDocListeners();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #swipeStartY = null;
  #swipeStartTime = null;
  #isSwipeGesture = false;
  #swipeAbortController = null;
  /** @type {AbortController | null} */
  #popoverDocAbort = null;

  #isDialogPopover() {
    return this.hasAttribute('data-dialog-popover');
  }

  #teardownPopoverDocListeners() {
    this.#popoverDocAbort?.abort();
    this.#popoverDocAbort = null;
  }

  #popoverDocumentMousedown = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.#isDialogPopover()) {
      requestAnimationFrame(() => {
        if (dialog.open) return;
        dialog.show();
        this.dispatchEvent(new DialogOpenEvent());
        this.#teardownPopoverDocListeners();
        this.#popoverDocAbort = new AbortController();
        const signal = this.#popoverDocAbort.signal;
        document.addEventListener('keydown', this.#handleKeyDown, { signal });
        document.addEventListener('mousedown', this.#popoverDocumentMousedown, { capture: true, signal });
      });
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.#focusContainerOnOpen();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      // Add swipe-to-close for bottom drawers on mobile
      if (dialog.classList.contains('dialog-drawer--bottom') && isMobileBreakpoint()) {
        this.#initSwipeToClose();
      }
    });
  }

  /**
   * Moves focus to the dialog container itself on open, instead of letting the
   * native `showModal()` focus the first focusable child (e.g. the close
   * button). Opt-in via the `focus-container-on-open` attribute on the
   * component. Keeps the dialog accessible — it is labelled via
   * `aria-labelledby`, so screen readers still announce it, and Tab moves on to
   * the real controls — while avoiding a `:focus-visible` ring on the close
   * button when the dialog is opened via pointer/touch.
   */
  #focusContainerOnOpen() {
    if (!this.hasAttribute('focus-container-on-open')) return;

    const { dialog } = this.refs;
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#isDialogPopover()) {
      this.#teardownPopoverDocListeners();
      if (dialog.open) {
        dialog.close();
        this.dispatchEvent(new DialogCloseEvent());
      }
      return;
    }

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#cleanupSwipeToClose();

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog from a dragged position with smooth fade animation.
   * The drawer continues moving down while fading out.
   * @param {number} currentTransform - The current transform value in pixels
   */
  #closeFromDragPosition(currentTransform) {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#cleanupSwipeToClose();

    // Calculate final position (move drawer completely off screen)
    const drawerHeight = dialog.offsetHeight || window.innerHeight;
    const finalTransform = drawerHeight + Math.max(currentTransform, 50); // Ensure it moves off screen
    
    // Set transition for smooth animation
    dialog.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out';
    dialog.style.transform = `translateY(${finalTransform}px)`;
    dialog.style.opacity = '0';
    dialog.classList.add('dialog-closing');

    // Clean up after animation
    setTimeout(async () => {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');
      dialog.style.opacity = '';
      dialog.style.transform = '';
      dialog.style.transition = '';
      dialog.style.removeProperty('--backdrop-opacity-dynamic');

      this.dispatchEvent(new DialogCloseEvent());
    }, 300);
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  /**
   * Initializes drag functionality for bottom drawers (both up and down).
   * Works specifically from the drag handle area at the top of the drawer.
   */
  #initSwipeToClose() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#swipeAbortController = new AbortController();
    const signal = this.#swipeAbortController.signal;

    const DRAG_HANDLE_AREA = 60; // Area from top where drag handle is (matches CSS)
    const DRAG_THRESHOLD = 3; // Minimum movement to start drag
    const CLOSE_THRESHOLD = 150; // Distance to close drawer when dragging down
    const VELOCITY_THRESHOLD = 0.3; // Velocity threshold for closing
    const MAX_UPWARD_DRAG = 100; // Maximum upward drag distance

    let initialY = 0;
    let initialTransform = 0;
    let isDragging = false;
    let startedInDragHandle = false;

    const isInDragHandleArea = (touchY) => {
      const drawerRect = dialog.getBoundingClientRect();
      const relativeY = touchY - drawerRect.top;
      const inArea = relativeY >= 0 && relativeY <= DRAG_HANDLE_AREA;
      return inArea;
    };

    const handleTouchStart = (e) => {
      // Don't interfere with clicks on interactive elements
      const target = e.target;
      if (target && (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a, button, [role="button"], [onclick]')
      )) {
        // Allow normal interaction with links and buttons
        return;
      }

      const touch = e.touches[0];
      if (!touch) {
        return;
      }

      // Check if touch is within the drawer bounds
      const drawerRect = dialog.getBoundingClientRect();
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      
      // Only proceed if touch is within drawer bounds
      if (touchX < drawerRect.left || touchX > drawerRect.right || 
          touchY < drawerRect.top || touchY > drawerRect.bottom) {
        return;
      }

      // Check if touch is in drag handle area (top 60px of drawer)
      startedInDragHandle = isInDragHandleArea(touchY);

      // Only allow dragging if started in drag handle area
      if (!startedInDragHandle) {
        return;
      }

      // Start tracking only if in drag handle area or drawer is already transformed
      this.#swipeStartY = touchY;
      this.#swipeStartTime = performance.now();
      initialY = touchY;
      
      // Get current transform value if any
      const currentTransform = dialog.style.transform;
      if (currentTransform) {
        const match = currentTransform.match(/translateY\(([^)]+)px\)/);
        initialTransform = match ? parseFloat(match[1]) || 0 : 0;
      } else {
        initialTransform = 0;
      }

      isDragging = false;
    };

    const handleTouchMove = (e) => {
      if (this.#swipeStartY === null) return;

      const touch = e.touches[0];
      if (!touch) return;

      const currentY = touch.clientY;
      const deltaY = currentY - this.#swipeStartY;
      const scrollTop = dialog.scrollTop;

      // Only start dragging if started in drag handle area
      if (!isDragging && Math.abs(deltaY) > DRAG_THRESHOLD) {
        // Only allow drag if started in drag handle area
        if (startedInDragHandle) {
          isDragging = true;
          // Disable all transitions and animations to allow manual transform
          dialog.style.transition = 'none';
          dialog.style.animation = 'none';
          dialog.style.animationName = 'none';
          dialog.classList.add('drawer-dragging');
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Not dragging, allow normal interaction (clicks, scrolls)
          this.#swipeStartY = null;
          return;
        }
      }

      // Apply drag transform
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();

        // Calculate new transform value
        let newTransform = initialTransform + deltaY;
        
        // Limit upward drag
        if (newTransform < -MAX_UPWARD_DRAG) {
          newTransform = -MAX_UPWARD_DRAG;
        }
        
        // Allow downward drag without limit (for closing)
        // Apply transform (animation is disabled via drawer-dragging class)
        dialog.style.transform = `translateY(${newTransform}px)`;

        // Update backdrop opacity based on downward drag only
        if (newTransform > 0) {
          const maxHeight = dialog.offsetHeight || window.innerHeight;
          const opacity = Math.max(0, 1 - (newTransform / maxHeight) * 1.5);
          dialog.style.setProperty('--backdrop-opacity-dynamic', opacity.toString());
        } else {
          // Reset backdrop opacity when dragging up
          dialog.style.setProperty('--backdrop-opacity-dynamic', '1');
        }

      }
    };

    const handleTouchEnd = (e) => {
      if (this.#swipeStartY === null) {
        return;
      }

      const touch = e.changedTouches[0];
      if (!touch) {
        return;
      }

      const currentY = touch.clientY;
      const deltaY = currentY - this.#swipeStartY;
      const timeDelta = performance.now() - (this.#swipeStartTime || 0);
      const velocity = timeDelta > 0 ? Math.abs(deltaY / timeDelta) : 0;

      // Get current transform
      const currentTransform = initialTransform + deltaY;

      // Remove dragging class to allow transitions
      dialog.classList.remove('drawer-dragging');

      if (isDragging) {
        // Close if dragged down enough or with sufficient downward velocity
        if (currentTransform > CLOSE_THRESHOLD || (deltaY > 0 && velocity > VELOCITY_THRESHOLD && currentTransform > 50)) {
          // Close from current position - continue moving down while fading out
          this.#closeFromDragPosition(currentTransform);
        } else {
          // Snap back smoothly to original position with animation
          dialog.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out';
          dialog.style.transform = '';
          dialog.style.removeProperty('--backdrop-opacity-dynamic');
          
          // Reset transition after animation completes
          setTimeout(() => {
            dialog.style.transition = '';
          }, 300);
        }
      } else {
        // Restore transition if not dragging
        dialog.style.transition = '';
      }

      this.#swipeStartY = null;
      this.#swipeStartTime = null;
      isDragging = false;
      startedInDragHandle = false;
      initialTransform = 0;
    };

    // touchstart is passive to allow normal clicks/scrolls
    // touchmove is non-passive only when we need to prevent default (during drag)
    dialog.addEventListener('touchstart', handleTouchStart, { passive: true, signal });
    dialog.addEventListener('touchmove', handleTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', handleTouchEnd, { passive: true, signal });
    dialog.addEventListener('touchcancel', handleTouchEnd, { passive: true, signal });
  }

  /**
   * Cleans up drag-to-close event listeners.
   */
  #cleanupSwipeToClose() {
    if (this.#swipeAbortController) {
      this.#swipeAbortController.abort();
      this.#swipeAbortController = null;
    }

    const { dialog } = this.refs;
    if (dialog) {
      dialog.style.transform = '';
      dialog.style.transition = '';
      dialog.style.animation = '';
      dialog.style.removeProperty('--backdrop-opacity-dynamic');
      dialog.classList.remove('drawer-dragging');
    }

    this.#swipeStartY = null;
    this.#swipeStartTime = null;
    this.#isSwipeGesture = false;
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
